import { createContext, useContext, type ReactNode } from "react";
import { OPEN_RECIPIENT_POLICY, type RecipientPolicy } from "@shared/types/notificationDomains";

/**
 * #1111: the tenant's Allowed notification domains and the domains of the people with access, as
 * the rules view carries them (`recipientPolicy`), for every address box and read-only list under
 * the Notifications tab. Outside a provider nothing is marked or refused.
 *
 * `held` is each rule's stored addresses, by `cellKey`: an address a rule already holds is kept
 * on save even when it is outside the list (the runner skips it at send), so the box refuses only
 * the ones a save adds, as the server does.
 */
interface RecipientPolicyValue {
  policy: RecipientPolicy;
  held: ReadonlyMap<string, readonly string[]>;
}

const NO_HELD: ReadonlyMap<string, readonly string[]> = new Map();

const RecipientPolicyContext = createContext<RecipientPolicyValue>({
  policy: OPEN_RECIPIENT_POLICY,
  held: NO_HELD,
});

export function RecipientPolicyProvider({
  policy,
  held = NO_HELD,
  children,
}: {
  policy: RecipientPolicy;
  held?: ReadonlyMap<string, readonly string[]>;
  children: ReactNode;
}) {
  return (
    <RecipientPolicyContext.Provider value={{ policy, held }}>
      {children}
    </RecipientPolicyContext.Provider>
  );
}

export function useRecipientPolicy(): RecipientPolicy {
  return useContext(RecipientPolicyContext).policy;
}

/** Every rule's stored addresses, by `cellKey`. */
export function useHeldAddresses(): ReadonlyMap<string, readonly string[]> {
  return useContext(RecipientPolicyContext).held;
}
